import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { DocsService, IDbTemplateItem } from '@shared/services/docs.service';
import { IPrinter, IPrintJobItem, PrintJobStatus, PrintService, truncatePrintError } from '@shared/services/print.service';
import { catchError, forkJoin, of } from 'rxjs';

type PrintJobsStatusFilter = PrintJobStatus | 'all';

interface ITableJobViewModel {
	createdAt: number;
	printer: string;
	status: PrintJobStatus;
	templateOrUrls: string;
	errorMessage: string;
}

@Component({
	selector: 'app-print-jobs',
	templateUrl: './print-jobs.component.html',
	styleUrl: './print-jobs.component.scss',
	standalone: false,
})
export class PrintJobsComponent implements AfterViewInit {
	displayedColumns: string[] = ['createdAt', 'printer', 'status', 'templateOrUrls', 'error'];
	dataSource = new MatTableDataSource<ITableJobViewModel>();
	isLoading = true;

	// 'all' means no status query param is sent - the endpoint returns every job regardless of status.
	statusFilter: PrintJobsStatusFilter = 'all';
	readonly statusOptions: PrintJobStatus[] = ['Pending', 'Processing', 'Failed', 'Succeeded'];

	private printerBySelf = new Map<string, IPrinter>();
	private templateBySelf = new Map<string, IDbTemplateItem>();

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_renew',
			text: () => 'print.refresh',
			click: () => this.updateList(),
		},
	];

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	constructor(
		private printService: PrintService,
		private docsService: DocsService,
	) {}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	onFilterChange(): void {
		// A user paged into a later page of the previous filter's results would otherwise see an
		// empty table if the new filter's result set has fewer pages - the paginator doesn't
		// reset its own page index just because the underlying data changed.
		this.paginator?.firstPage();
		this.updateList();
	}

	// job.printer is the printer's self-ref (e.g. /print/printers/{id}); resolve it against the
	// printers list for a human-readable "(alias) description" label, falling back to the raw ref
	// when the printer can't be found (e.g. deleted since the job ran).
	private getPrinterLabel(printerRef: string): string {
		const printer = this.printerBySelf.get(printerRef);
		return printer ? `(${printer.alias}) ${printer.description}` : printerRef;
	}

	// A job was created either with a template (docs template self-ref) or with raw urls, never
	// both - resolve whichever is present, falling back to the raw ref when the template can't be
	// found (e.g. deleted since the job ran).
	private getTemplateOrUrls(job: IPrintJobItem): string {
		if (job.template) {
			const template = this.templateBySelf.get(job.template);
			return template ? template.name : job.template;
		}
		return job.urls?.join(', ') ?? '';
	}

	private getTableViewModel(job: IPrintJobItem): ITableJobViewModel {
		return {
			createdAt: job.meta.createdAt,
			printer: this.getPrinterLabel(job.printer),
			status: job.status,
			templateOrUrls: this.getTemplateOrUrls(job),
			errorMessage: job.errorMessage ?? '',
		};
	}

	updateList(): void {
		this.isLoading = true;
		const status = this.statusFilter === 'all' ? undefined : this.statusFilter;

		// Printer/template lookups are display-only enrichment - a user with print:LabelExecutionList
		// but not printer/template read access must still see their jobs, so those two calls fail
		// soft into an empty list rather than taking the whole forkJoin down with them.
		forkJoin({
			printers: this.printService.getPrinters().pipe(catchError(() => of<IPrinter[]>([]))),
			templates: this.docsService.getTemplateList().pipe(catchError(() => of<{ result: IDbTemplateItem[] }>({ result: [] }))),
			jobs: this.printService.getPrintJobs(status),
		}).subscribe({
			next: ({ printers, templates, jobs }) => {
				this.printerBySelf = new Map(printers.map((printer) => [printer.self, printer]));
				this.templateBySelf = new Map(templates.result.map((template) => [template.self, template]));
				this.dataSource.data = jobs.items.map((job) => this.getTableViewModel(job));
				this.onSort(this.sort);
				this.isLoading = false;
			},
			// Unlike the printer/template enrichment above, a failed getPrintJobs() call has no
			// soft fallback - the primary data is simply unavailable. Clear the table rather than
			// leaving stale rows on screen, and always drop isLoading so refresh is retryable.
			error: () => {
				this.dataSource.data = [];
				this.isLoading = false;
			},
		});
	}

	public onSort(sort: Sort): void {
		if (sort.direction === '') {
			this.sort.sort({ id: 'createdAt', start: 'desc', disableClear: true });
		}
	}

	jobStatusClass(status: string): string {
		switch (status) {
			case 'Failed':
				return 'print-job-status--failed';
			case 'Succeeded':
				return 'print-job-status--done';
			default:
				return 'print-job-status--working';
		}
	}

	truncateError(message: string): string {
		return truncatePrintError(message);
	}
}
